import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Trash2, Calendar, User, MessageSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/contact/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.submissions || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast({
        title: "Fehler",
        description: "Nachrichten konnten nicht geladen werden",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (messageId, senderName) => {
    if (!window.confirm(`Moechten Sie die Nachricht von "${senderName}" wirklich loeschen?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${BACKEND_URL}/api/contact/submissions/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Geloescht",
        description: "Nachricht wurde erfolgreich geloescht"
      });
      
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast({
        title: "Fehler",
        description: "Nachricht konnte nicht geloescht werden",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unbekannt';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceLabel = (service) => {
    const labels = {
      'hunde': 'Hunde-Fotografie',
      'portraits': 'Portrait-Fotografie',
      'events': 'Event-Fotografie',
      'landschaft': 'Landschafts-Fotografie',
      'andere': 'Andere'
    };
    return labels[service] || service || 'Nicht angegeben';
  };

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Wird geladen...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/settings')}
            className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Zurueck zu Einstellungen
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Kontaktanfragen</h1>
              <p className="text-gray-600 mt-2">
                {messages.length} {messages.length === 1 ? 'Nachricht' : 'Nachrichten'} erhalten
              </p>
            </div>
          </div>
        </div>

        {messages.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Noch keine Kontaktanfragen vorhanden</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div key={message.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center">
                      <User size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{message.name}</h3>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(message.submitted_at)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(message.id, message.name)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    title="Loeschen"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail size={16} className="text-primary" />
                    <a href={`mailto:${message.email}`} className="hover:text-primary">
                      {message.email}
                    </a>
                  </div>
                  {message.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone size={16} className="text-primary" />
                      <a href={`tel:${message.phone}`} className="hover:text-primary">
                        {message.phone}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">
                      {getServiceLabel(message.service)}
                    </span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMessages;
